import { useMemo } from "react";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "Hooks";
import {
  selectAutoApprovalEnabled,
  selectCurrentLevel,
  selectOverrides,
  selectRevertOverrides,
  setAutoApproval,
  setRlLevel,
} from "../store/policyConfigSlice";
import { NormalizedPolicies, Override, RlScanLevel } from "../types";
import { isPolicyCustomizingLevel } from "../utils";
import useConfigurationProvider from "./useConfigurationProvider";

const getDerivedScanLevel = (
  rlScanLevel: RlScanLevel,
  currentLevel: number | null,
): number | null => {
  let derivedScanLevel = null;

  if (currentLevel !== null) {
    derivedScanLevel = currentLevel;
  } else if (rlScanLevel.current_level !== null) {
    derivedScanLevel = rlScanLevel.current_level;
  } else if (rlScanLevel.inherited_level !== null) {
    derivedScanLevel = rlScanLevel.inherited_level;
  }

  return derivedScanLevel;
};

const getCustomizedPolicies = (
  policies: NormalizedPolicies,
  overrides: Override[],
  reverts: string[],
  derivedScanLevel: number | null,
): string[] => {
  const policyList = Object.values(policies);

  return policyList
    .filter((policy) => {
      return isPolicyCustomizingLevel({
        policy: policy,
        overrides: overrides,
        reverts: reverts,
        scanLevel: derivedScanLevel ?? 0,
      });
    })
    .map((policy) => policy.id);
};

type DerivedScanLevel = {
  derivedScanLevel: number | null;
  canDisableLevels: boolean;
  customizedPolicies: string[];
  isDirty: boolean;
  handleLevelChange: (level: number | null) => void;
  clearLevel: () => void;
  currentAutoApprovalEnabled?: boolean;
  updateAutoApproval: (enabled: boolean) => void;
};

const useDerivedScanLevel = (): DerivedScanLevel => {
  const { groupId, organizationId } = useParams();
  const dispatch = useAppDispatch();
  const overrides = useAppSelector(selectOverrides);
  const reverts = useAppSelector(selectRevertOverrides);

  const { rlScanLevel, policies, autoApprovalEnabled } =
    useConfigurationProvider();

  const currentLevel = useAppSelector(selectCurrentLevel);

  const currentAutoApprovalEnabled = useAppSelector(selectAutoApprovalEnabled);
  const updateAutoApproval = (enabled: boolean) => {
    dispatch(setAutoApproval(enabled));
  };

  const orgHasDisabledLevels =
    rlScanLevel.inherited_level === 0 && groupId !== undefined;

  const derivedScanLevel = useMemo(
    () => getDerivedScanLevel(rlScanLevel, currentLevel),
    [rlScanLevel, currentLevel],
  );

  const customizedPolicies = useMemo(
    () => getCustomizedPolicies(policies, overrides, reverts, derivedScanLevel),
    [policies, overrides, reverts, derivedScanLevel],
  );

  const handleLevelChange = (level: number | null) => {
    const isUsingLevels = derivedScanLevel !== 0;

    if (level !== derivedScanLevel) {
      if (!isUsingLevels && level === null) {
        dispatch(
          setRlLevel(
            rlScanLevel.inherited_level === 0 ? 5 : rlScanLevel.inherited_level,
          ),
        );
      } else {
        dispatch(setRlLevel(level));
      }

      const changeToNotUsingLevels =
        level === 0 || (level === null && rlScanLevel.current_level === 0);
      if (
        changeToNotUsingLevels &&
        (currentAutoApprovalEnabled !== undefined ||
          autoApprovalEnabled === true)
      ) {
        dispatch(setAutoApproval(false));
      }
    }
    // Level is same as current level, and was not modified
    else {
      dispatch(setRlLevel(null));
    }
  };

  const clearLevel = () => {
    handleLevelChange(null);
    if (
      rlScanLevel.current_level !== 0 &&
      currentAutoApprovalEnabled !== undefined
    ) {
      dispatch(setAutoApproval(undefined));
    }
  };

  const canDisableLevels = organizationId !== undefined || orgHasDisabledLevels;
  // Undo unsaved change to not use levels if that option was disabled in the meantime and shouldn't be selected
  if (!canDisableLevels && currentLevel === 0) {
    handleLevelChange(null);
  }

  // If anything changed on the BE so that the level is 0 and auto approval is enabled in unsaved changes revert them
  if (derivedScanLevel === 0 && currentAutoApprovalEnabled) {
    dispatch(setAutoApproval(undefined));
  }

  return {
    derivedScanLevel,
    handleLevelChange,
    customizedPolicies,
    canDisableLevels,
    isDirty: currentLevel !== null || currentAutoApprovalEnabled !== undefined,
    clearLevel,
    currentAutoApprovalEnabled,
    updateAutoApproval: updateAutoApproval,
  };
};

export default useDerivedScanLevel;
